import { useState, useEffect } from 'react';
import { OnboardingState } from '../OnboardingWizard';
import { createGDAPRelationship, GDAPRole } from '../../../services/api';
import {
  ShieldCheck,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface Props {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  onNext: () => void;
  setError: (error: string | null) => void;
}

export default function CreateGDAPStep({ state, updateState, onNext, setError }: Props) {
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(!!state.relationshipId);
  const [roles, setRoles] = useState<GDAPRole[]>([]);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [isExisting, setIsExisting] = useState(false);

  useEffect(() => {
    // If already created in a previous visit to this step, don't re-create
    if (state.relationshipId) {
      setCreated(true);
    }
  }, [state.relationshipId]);

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await createGDAPRelationship({
        companyName: state.companyName,
      });

      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      setRoles(result.roles || []);
      setIsExisting(!!result.existing);
      updateState({
        relationshipId: result.relationshipId,
        approvalUrl: result.approvalUrl,
        gdapStatus: result.status,
      });
      setCreated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create GDAP relationship');
    } finally {
      setLoading(false);
    }
  };

  const displayedRoles = showAllRoles ? roles : roles.slice(0, 6);

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Create GDAP Relationship
            </h2>
            <p className="text-sm text-muted-foreground">
              Request delegated admin access with 26 Microsoft Entra roles
            </p>
          </div>
        </div>
      </div>

      {!created ? (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
              What happens next:
            </h3>
            <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
              <li className="flex items-start">
                <span className="mr-2">1.</span>
                A GDAP relationship request will be created with 26 admin roles
              </li>
              <li className="flex items-start">
                <span className="mr-2">2.</span>
                You'll receive an approval URL to send to the customer
              </li>
              <li className="flex items-start">
                <span className="mr-2">3.</span>
                The customer's Global Admin must approve the request
              </li>
            </ul>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              Roles to be requested:
            </h4>
            <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
              <div>User Administrator</div>
              <div>License Administrator</div>
              <div>Helpdesk Administrator</div>
              <div>Exchange Administrator</div>
              <div>Security Administrator</div>
              <div>Intune Administrator</div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              +20 more roles for comprehensive MSP management
            </p>
          </div>

          <button
            onClick={handleCreate}
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Creating GDAP Relationship...
              </>
            ) : (
              'Create GDAP Relationship'
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className={`rounded-lg p-4 ${isExisting ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
            <div className="flex items-center">
              <CheckCircle className={`h-5 w-5 mr-2 ${isExisting ? 'text-blue-500' : 'text-green-500'}`} />
              <h3 className={`font-medium ${isExisting ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>
                {isExisting ? 'Existing GDAP Relationship Found' : 'GDAP Relationship Created'}
              </h3>
            </div>
            <p className={`mt-2 text-sm ${isExisting ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-300'}`}>
              {isExisting && (
                <span className="block mb-1">A relationship for this client was already created and is pending approval.</span>
              )}
              Relationship ID: <code className={`px-1 rounded ${isExisting ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-green-100 dark:bg-green-900/50'}`}>{state.relationshipId}</code>
            </p>
          </div>

          {roles.length > 0 && (
            <div className="bg-muted rounded-lg p-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                {roles.length} Admin Roles Included:
              </h4>
              <div className="space-y-1">
                {displayedRoles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center text-sm text-muted-foreground"
                  >
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    {role.name}
                  </div>
                ))}
              </div>
              {roles.length > 6 && (
                <button
                  onClick={() => setShowAllRoles(!showAllRoles)}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center"
                >
                  {showAllRoles ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Show all {roles.length} roles
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-border">
            <button onClick={onNext} className="btn-primary w-full sm:w-auto">
              Continue to Customer Approval &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
